<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import {
  UICheckbox,
  UIFormItem,
  UIModal,
  UIModalFooter,
  UIModalHeader,
  UISelect,
} from '@gohighlevel/ghl-ui'
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarService } from '../../../class/services/CalendarService'
import { useConflictingCalendars } from '../../../composables/useConflictingCalendars'
import AppState, { calendarOptions } from '../../../states/app'
import ConflictingCalendarsTable from './ConflictingCalendarsTable.vue'
const { t } = useI18n()

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  group: {
    type: Object,
    required: true,
  },
  onCancelClick: {
    type: Function,
    required: true,
  },
  onConfirmClick: {
    type: Function,
    required: true,
  },
})

const state = reactive({
  showConflictingCalendars: false,
  selectedCalendars: [] as string[],
  consent: false,
  isLoading: false,
  showSuccessAlert: false,
})

const { getConflictingCalendars, getConflictingCalendarsTable } =
  useConflictingCalendars()

const conflictingCalendars = computed(() => {
  return getConflictingCalendars(state.selectedCalendars, props.group.id)
})

const conflictingCalendarsTable = computed(() => {
  return getConflictingCalendarsTable(conflictingCalendars.value)
})

const onContinueClick = async () => {
  if (
    conflictingCalendarsTable.value.length > 0 &&
    !state.showConflictingCalendars
  ) {
    state.showConflictingCalendars = true
  } else {
    await moveCalendars()
    props.onConfirmClick()
  }
}

const moveCalendars = async () => {
  try {
    state.isLoading = true
    await CalendarService.moveToGroup(
      AppState.locationId,
      props.group.id,
      undefined,
      state.selectedCalendars
    )
    state.showSuccessAlert = true
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  } finally {
    state.isLoading = false
  }
}

const modalTitle = computed(() => {
  return state.showConflictingCalendars
    ? t('settings.calendar.conflicting_calendars')
    : t('settings.calendar.add_calendars_to_group')
})

const modalDescription = computed(() => {
  return state.showConflictingCalendars
    ? t('settings.calendar.conflicting_calendars_description', {
        name: props.group.name,
      })
    : t('settings.calendar.select_calendars_to_add', {
        name: props.group.name,
      })
})

const disableContinueButton = computed(() => {
  if (state.showConflictingCalendars) {
    return !state.consent
  }
  return !state.selectedCalendars.length
})

const onClickCancel = () => {
  if (state.showConflictingCalendars) {
    state.showConflictingCalendars = false
  } else {
    props.onCancelClick()
  }
}

const positiveButtonText = computed(() => {
  return conflictingCalendarsTable.value.length > 0
    ? t('settings.calendar.continue')
    : t('settings.calendar.confirm')
})
</script>

<template>
  <div class="relative">
    <UIModal
      id="generalConfirmationModal"
      :show="show"
      :width="state.showConflictingCalendars ? 590 : 490"
      class="z-50"
    >
      <template #header>
        <UIModalHeader
          id="generalConfirmationModalHeader"
          :type="'primary'"
          :title="modalTitle"
          @close="onCancelClick"
        />
      </template>

      <div>
        <div
          class="text-[14px] font-normal text-gray-500"
          v-html="modalDescription"
        />

        <UIFormItem
          v-if="!state.showConflictingCalendars"
          class="my-[10px]"
          :label="t('settings.calendar.add_calendars')"
          :required="true"
          :show-feedback="false"
        >
          <UISelect
            id="select-calendars"
            v-model:value="state.selectedCalendars"
            :options="calendarOptions"
            :max-tag-count="2"
            :multiple="true"
            :filterable="true"
            :clearable="true"
          >
            <template #empty>
              <div class="text-[14px] font-normal text-gray-500">
                {{ t('settings.calendar.no_calendars_found_title') }}
              </div>
            </template>
          </UISelect>
        </UIFormItem>

        <div v-else>
          <ConflictingCalendarsTable :items="conflictingCalendarsTable" />

          <div class="mb-[10px] mt-[20px]">
            <UICheckbox v-model:checked="state.consent" size="medium">
              <div class="text-[14px] font-normal text-gray-500">
                {{
                  t('settings.calendar.move_calendar_consent', {
                    name: props.group.name,
                  })
                }}
              </div>
            </UICheckbox>
          </div>
        </div>
      </div>

      <template #footer>
        <UIModalFooter
          id="generalConfirmationModalFooter"
          :positive-text="positiveButtonText"
          :negative-text="
            state.showConflictingCalendars
              ? t('settings.calendar.back')
              : t('settings.calendar.cancel')
          "
          :type="'primary'"
          :loading="state.isLoading"
          :disabled="disableContinueButton || state.isLoading"
          @positive-click="onContinueClick"
          @negative-click="onClickCancel"
        />
      </template>
    </UIModal>
  </div>
</template>
